import * as React from "react";

import { Field, FormRenderProps } from "@progress/kendo-react-form";
import { StackLayout } from "@progress/kendo-react-layout";
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';

import { FormDropDownList, FormInput, FormMultiSelect } from "../FormComponents";
import { requiredValidator } from "../validators";

import { useAppSelector } from "../../store/store";

const prefaceQuestionnaire: string = "<h2 style=\"text-align: center;\"><strong>Referral Questionnaire<\/strong><\/h2>" +
    "Community Veterinary Outreach's program provides preventative veterinary care only.<br/><br/>Pet owners should NOT wait for a clinic if they have a sick or injured pet.They should be strongly advised to seek veterinary care immediately.<br/><br/>" +
    "Referred clients must meet all of the following criteria:<br/><br/>" +
    "1. Client is homeless, transitionally-housed or vulnerably-housed (CHRN definition of homeless)<br/>" +
    "2. Client is receiving ODSP, OW, or has no income; or if the client has no ID - is making regular use of shelter or agency services<br/>" +
    "3. Client does NOT have an existing veterinarian - client - patient relationship(VPCR), defined as having their animal examined or treated by a veterinarian in the past 12 months.<br/><br/>" +
    "All information collected is strictly confidential and during analysis of data all personal and/or identifying information will be stripped and destroyed. This questionnaire is voluntary and veterinary services will not be withheld if any or all of the questions, other than essential client and pet information needed by law, are not answered.<br/>" +
    "Answering questions regarding pet bites will not result in a legal complaint by Community Veterinary Outreach.<br/><br/>" +
    "If there are any questions while completing this form, please e-mail info@vetoutreach.org for further direction.";

export interface ExamNewQuestionnaireProps {
    form: any;
}
const ExamNewQuestionnaire = ({
    form
}: ExamNewQuestionnaireProps) => {

    const auth = useAppSelector((state) => state.auth);
    const [isVancouver, setIsVancouver] = React.useState<boolean>((auth!.user!.region === 7) || (auth!.user!.region === 1));

    const [tobaccoUse, setTobaccoUse] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (form) {
            setTobaccoUse(form.current && form.current.valueGetter && (form.current.valueGetter("A385") === 'Yes'));
        }
    }, [form]);


    const onTobaccoUseChange = (e: DropDownListChangeEvent) => {
        setTobaccoUse(e.value === "Yes");
    }

    return (
        <div className={"sds-questionnaire"}>
            <span dangerouslySetInnerHTML={{ __html: prefaceQuestionnaire }} />

            <fieldset
                style={{}}
                className={"k-form-fieldset"}
            >
                <legend
                    style={{ paddingTop: "24px" }}
                    className={"k-form-legend"}>
                    Client Information
                </legend>

                <StackLayout gap={48}>
                    <Field
                        label={"With which gender do you identify?*"}
                        name={"A169"}
                        component={FormDropDownList}
                        data={["N/A", "Male", "Female", "Transgender", "Non-binary", "Not listed", "Prefer not to Disclose"]}
                        validator={requiredValidator}
                    />
                    <Field
                        label={"Notes"}
                        name={"N169"}
                        component={FormInput}
                    />
                </StackLayout>
                <StackLayout gap={48}>
                    <Field
                        label={"Preferred Personal Pronouns"}
                        name={"A170"}
                        component={FormMultiSelect}
                        autoClose={false}
                        header={<span style={{ float: "right", paddingRight: "32px", fontStyle: "italic" }}>Click here when done</span>}
                        data={["He/Him/His", "She/Her/Hers", "They/Them/Theirs", "Ze/Hir", "Not Listed"]}
                    />
                    <Field
                        label={"\""}
                        name={"N170"}
                        component={FormInput}
                    />
                </StackLayout>
                <StackLayout gap={48} >
                    <Field
                        label={"Client Age (years)"}
                        name={"A168"}
                        component={FormInput}
                    />
                    <Field
                        label={"\""}
                        name={"N168"}
                        component={FormInput}
                    />
                </StackLayout>
                <StackLayout gap={48}>
                    <Field
                        label={"Income Source"}
                        name={"A171"}
                        component={FormMultiSelect}
                        autoClose={false}
                        header={<span style={{ float: "right", paddingRight: "32px", fontStyle: "italic" }}>Click here when done</span>}
                        data={["Income support or assistance", "Disability support", "Personal needs allowance",
                            "Child and family benefits", "Seniors Supplement", "Employment Income", "Other (Please Describe)"]}
                    />
                    <Field
                        label={"\""}
                        name={"N171"}
                        component={FormInput}
                    />
                </StackLayout>
                <Field
                    label={"Total Monthly Household Income"}
                    name={"A172"}
                    component={FormInput}
                />
                <StackLayout gap={48}>
                    <Field
                        label={"Marital Status*"}
                        name={"A173"}
                        component={FormDropDownList}
                        data={["N/A", "Single", "Married", "Common-law", "Separated", "Divorced", "Widow/Widower", "Other (Text Box)"]}
                        validator={requiredValidator}
                    />
                    <Field
                        label={"\""}
                        name={"N173"}
                        component={FormInput}
                    />
                </StackLayout>
                <StackLayout gap={48}>
                    <Field
                        label={"Number of dependents in household"}
                        name={"A174"}
                        component={FormInput}
                        hint={"Dependents are defined as 1) persons under 22 years of age who do not have a spouse or partner OR " +
                            "2) those over 22 years of age who depend on their parents since before aged 22 and who cannot support " +
                            "themselves because of a mental or physical condition"}
                    />
                    <Field
                        label={"\""}
                        name={"N174"}
                        component={FormInput}
                    />
                </StackLayout>
                <Field
                    label={"Client’s current housing status*"}
                    name={"A175"}
                    component={FormMultiSelect}
                    autoClose={false}
                    header={<span style={{ float: "right", paddingRight: "32px", fontStyle: "italic" }}>Click here when done</span>}
                    data={["Unsheltered", "Living in public or private spaces without consent or contract", "Living in places not intended for permanent human habitation",
                        "Emergency sheltered - living in emergency shelters e.g. homeless, domestic/family violence, natural disaster/fire)",
                        "Provisionally accommodated", "Living in interim/transitional housing", "Living temporarily with others (couch surfing, hidden homeless)",
                        "Living in short term, temporary rental accommodations", "Living in institutional care without permanent housing (penal institution, medical/mental health institution, residential treatment or withdrawal management centres, childrens institution/group home)",
                        "Living in accommodation/reception centres for recently arrived immigrants and refugees",
                        "At risk of homelessness", "Precarious employment", "Sudden unemployment", "Loss of supports", "Facing eviction",
                        "Severe and/or persistent mental illness and/or active substance use", "Domestic/family violence", "Inadequate/unsuited institutional care"]}
                    validator={requiredValidator}
                />
                <legend
                    style={{ paddingTop: "24px" }}
                    className={"k-form-legend"}>
                    Pet Ownership Information
                </legend>
                <StackLayout gap={48}>
                    <Field
                        label={"Have you had any difficulty seeking shelter or housing because you have a pet in the past year?*"}
                        name={"A176"}
                        component={FormDropDownList}
                        data={["N/A", "Yes", "No"]}
                        validator={requiredValidator}
                    />
                    <Field
                        label={"Notes"}
                        name={"N176"}
                        component={FormInput}
                    />
                </StackLayout>
                <StackLayout gap={48}>
                    <Field
                        label={"Do you feel that having a pet has influenced your own health?*"}
                        name={"A177"}
                        component={FormDropDownList}
                        data={["N/A", "Yes", "No", "I don't know"]}
                        validator={requiredValidator}
                    />
                    <Field
                        label={"\""}
                        name={"N177"}
                        component={FormInput}
                    />
                </StackLayout>
                <Field
                    label={"If YES, please tell us in what way"}
                    name={"A178"}
                    component={FormInput}
                />
                <StackLayout gap={48}>
                    <Field
                        label={"I need support arranging transportation to/from the clinic.*"}
                        name={"A179"}
                        component={FormDropDownList}
                        data={["Yes", "No", "N/A"]}
                        hint={"Not always available at every clinic, but we will notify you when we call to schedule your appointment"}
                        validator={requiredValidator}
                    />
                    <Field
                        label={"\""}
                        name={"N179"}
                        component={FormInput}
                    />
                </StackLayout>
                <div
                    style={{ paddingTop: "24px", fontWeight: "bold" }}>
                    We are dedicated to improving the health of both people and pets, and may have human healthcare and social support services available at veterinary outreach clinics. To help us understand how we can best support you, please answer the following questions
                </div>
                {!isVancouver && (<div>
                    <StackLayout gap={48}>
                        <Field
                            label={"Have you smoked cigarettes in the past 30 days?*"}
                            name={"A180"}
                            component={FormDropDownList}
                            data={["Yes", "No"]}
                            validator={requiredValidator}
                        />
                        <Field
                            label={"Notes"}
                            name={"N180"}
                            component={FormInput}
                        />
                    </StackLayout>
                    <StackLayout gap={48}>
                        <Field
                            label={"Do you smoke cigarettes every day, occasionally or not at all?*"}
                            name={"A181"}
                            component={FormDropDownList}
                            data={["N/A", "Daily", "Occasionally", "Don't know/prefer not to say", "Not at all"]}
                            validator={requiredValidator}
                        />
                        <Field
                            label={"\""}
                            name={"N181"}
                            component={FormInput}
                        />
                    </StackLayout>
                </div>)}
                {isVancouver && (<div>
                    <StackLayout gap={48}>
                        <Field
                            label={"Do you use any form of tobacco, vape, or cannabis?*"}
                            name={"A385"}
                            component={FormDropDownList}
                            data={["Yes", "No", "Prefer Not to Answer"]}
                            onChange={onTobaccoUseChange}
                            validator={requiredValidator}
                        />
                        <Field
                            label={"Notes"}
                            name={"N385"}
                            component={FormInput}
                        />
                    </StackLayout>
                    {(tobaccoUse && <div>
                        <StackLayout gap={48}>
                            <Field
                                label={"Have you used any form of tobacco, vape, or cannabis in the past 7 days?*"}
                                name={"A386"}
                                component={FormDropDownList}
                                data={["Yes", "No"]}
                                validator={requiredValidator}
                            />
                            <Field
                                label={"Notes"}
                                name={"N386"}
                                component={FormInput}
                            />
                        </StackLayout>
                        <Field
                            label={"Which of the following have you used (select all that apply):*"}
                            name={"A387"}
                            component={FormMultiSelect}
                            autoClose={false}
                            header={<span style={{ float: "right", paddingRight: "32px", fontStyle: "italic" }}>Click here when done</span>}
                            data={["Cigarettes", "E-cigarettes/vapes", "Cigar", "Pipe", "Smokeless tobacco/Nicotine pouches", "Tobacco mixed with cannabis"]}
                            validator={requiredValidator}
                        />
                        <StackLayout gap={48}>
                            <Field
                                label={"Are you interested in making a change to your smoking?*"}
                                name={"A388"}
                                component={FormDropDownList}
                                data={["Yes", "No"]}
                                validator={requiredValidator}
                            />
                            <Field
                                label={"Notes"}
                                name={"N388"}
                                component={FormInput}
                            />
                        </StackLayout>
                        <StackLayout gap={48}>
                            <Field
                                label={"Cutting back (harm reduction)?*"}
                                name={"A389"}
                                component={FormDropDownList}
                                data={["Yes", "No"]}
                                validator={requiredValidator}
                            />
                            <Field
                                label={"Notes"}
                                name={"N389"}
                                component={FormInput}
                            />
                        </StackLayout>
                        <StackLayout gap={48}>
                            <Field
                                label={"Quitting?"}
                                name={"A390"}
                                component={FormDropDownList}
                                data={["Yes", "No"]}
                                validator={requiredValidator}
                            />
                            <Field
                                label={"Notes"}
                                name={"N390"}
                                component={FormInput}
                            />
                        </StackLayout>
                        <StackLayout gap={48}>
                            <Field
                                label={"Would you be interested in free patches/gum/lozenge to help you with this?*"}
                                name={"A391"}
                                component={FormDropDownList}
                                data={["Yes", "No"]}
                                validator={requiredValidator}
                            />
                            <Field
                                label={"Notes"}
                                name={"N391"}
                                component={FormInput}
                            />
                        </StackLayout>
                        <StackLayout gap={48}>
                            <Field
                                label={"What are the main reasons you would like to quit or reduce smoking or vaping (select all that apply):*"}
                                name={"A392"}
                                component={FormMultiSelect}
                                autoClose={false}
                                header={<span style={{ float: "right", paddingRight: "32px", fontStyle: "italic" }}>Click here when done</span>}
                                data={["Your own health and wellbeing", "The health and wellbeing of others in your life",
                                    "Your pet’s health and wellbeing", "Advice or pressure from your friends or family",
                                    "Advice or pressure from your doctor or other healthcare provider(s)", "To save money"]}
                                validator={requiredValidator}
                            />
                            <Field
                                label={"Other"}
                                name={"N392"}
                                component={FormInput}
                            />
                        </StackLayout>
                        <StackLayout gap={48}>
                            <Field
                                label={"From what you selected above as your main reasons for wanting to quit or reduce, what would you say is your number one, top reason?*"}
                                name={"A393"}
                                component={FormDropDownList}
                                data={["Your own health and wellbeing", "The health and wellbeing of others in your life",
                                    "Your pet’s health and wellbeing", "Advice or pressure from your friends or family",
                                    "Advice or pressure from your doctor or other healthcare provider(s)", "To save money", "Other"]}
                                validator={requiredValidator}
                            />
                            <Field
                                label={"Notes"}
                                name={"N393"}
                                component={FormInput}
                            />
                        </StackLayout>
                    </div>)}
                </div>)}
                <StackLayout gap={48}>
                    <Field
                        label={"Where do you usually go when you need immediate care for a minor health problem?"}
                        name={"A182"}
                        component={FormMultiSelect}
                        autoClose={false}
                        header={<span style={{ float: "right", paddingRight: "32px", fontStyle: "italic" }}>Click here when done</span>}
                        data={["A doctors office", "A hospital outpatient clinic", "A community health centre (or CLSC)", "A walk-in clinic",
                            "A hospital emergency room", "Other (Please Describe)", "Outreach nurse", "Other social service setting (e.g. CMHA)",
                            "CVO One Health Clinic", "Don't know/prefer not to say"]}
                    />
                    <Field
                        label={"\""}
                        name={"N182"}
                        component={FormInput}
                    />
                </StackLayout>
                <StackLayout gap={48}>
                    <Field
                        label={"What community supports would you like to receive?"}
                        name={"A183"}
                        component={FormMultiSelect}
                        autoClose={false}
                        header={<span style={{ float: "right", paddingRight: "32px", fontStyle: "italic" }}>Click here when done</span>}
                        data={["Smoking Cessation Services", "Healthy Eating / Active Living Services", "Flu Vaccination", "Dental Screening",
                            "Housing support", "Primary health care", "Mental health support", "Addictions support",
                            "Substance use support", "COVID information", "Other (Please Describe)"]}
                    />
                    <Field
                        label={"\""}
                        name={"N183"}
                        component={FormInput}
                    />
                </StackLayout>
                <StackLayout gap={48} >
                    <Field
                        label={"Do you identify as a visible minority or as a member of a racialized community?*"}
                        name={"A184"}
                        component={FormDropDownList}
                        data={["Yes", "No", "Prefer not to say"]}
                        validator={requiredValidator}
                    />
                    <Field
                        label={"Notes"}
                        name={"N184"}
                        component={FormInput}
                    />
                </StackLayout>
                <StackLayout gap={48} style={{ paddingBottom: "24px" }}>
                    <Field
                        label={"Do you identify as First Nations, Métis, Inuit, or Indigenous background?"}
                        name={"A384"}
                        component={FormDropDownList}
                        data={["Yes", "No", "Prefer not to say"]}
                    />
                    <Field
                        label={"Notes"}
                        name={"N384"}
                        component={FormInput}
                    />
                </StackLayout>
            </fieldset>
        </div>
    );
}

export default ExamNewQuestionnaire;
